import { useState, useEffect, useRef, useMemo } from 'react'
import { trackInternalEvent } from '@rio/tracking'
import { useDispatch } from 'react-redux'
import _debounce from 'lodash/debounce'
import { Icon24 } from 'builder/components/Icon'
import { useClickOutside } from 'builder/hooks/useClickOutside'
import { actions } from 'builder/modules/resumeEditor'
import { i18n as I18n } from 'builder/utils/i18n'
import { UseExampleEvents, UseExampleEventLabels } from 'builder/modules/constants'
import { useDebouncedEffect } from 'builder/hooks/useDebouncedEffect'
import {
  JobTitleSearchContainer,
  JobTitleSearch,
  SuggestedTitlesContainer,
  SuggestedTitleList,
  SuggestedTitleItem,
  NoResultsContainer,
  NoResults,
  NoResultsImg,
  NoResultsText,
  AnotherOptionBtn,
} from '../styles'
import { useResumeExampleContext } from '../ResumeExampleContext'
import emptySearch from './empty-search.svg'

const JobTitle = () => {
  const dispatch = useDispatch()

  const { jobTitles, loadMore, searchedTitle, setSearchedTitle } = useResumeExampleContext()

  const [suggestedTitles, setSuggestedTitles] = useState<string[]>(jobTitles)
  const [showSuggestedTitles, setShowSuggestedTitles] = useState<boolean>(false)

  const ref = useRef<HTMLDivElement>(null)

  useClickOutside(ref, () => setShowSuggestedTitles(false))

  useEffect(() => {
    setSuggestedTitles(
      jobTitles.filter(title => title.toLowerCase().includes(searchedTitle.toLowerCase().trim())),
    )
  }, [searchedTitle, jobTitles])

  useDebouncedEffect(
    () => {
      if (searchedTitle && !suggestedTitles.length) {
        trackInternalEvent(UseExampleEvents.seeEmptySearchResults, {
          label: UseExampleEventLabels.examplesPrefill,
          searchQuery: searchedTitle,
        })
      }
    },
    1000,
    [searchedTitle, suggestedTitles.length],
  )

  const debouncedLoadMore = useMemo(() => {
    return _debounce(() => loadMore(true), 500)
  }, [loadMore])

  const onTitleChange = (e: React.BaseSyntheticEvent) => {
    setSearchedTitle(e.target.value)
    debouncedLoadMore()
  }

  const onSelectTitle = (title: string) => {
    trackInternalEvent(UseExampleEvents.clickSearchResult, {
      label: UseExampleEventLabels.examplesPrefill,
      jobTitle: title,
    })
    setSearchedTitle(title)
    setShowSuggestedTitles(false)
    loadMore(true)
  }

  const onStartWithAnotherOption = () => {
    trackInternalEvent(UseExampleEvents.clickStartWithAnotherOption, {
      label: UseExampleEventLabels.examplesPrefill,
    })
    dispatch(actions.setShowUseExamplePopup(false))
    dispatch(actions.setShowPrefillResumeModal(true))
  }

  return (
    <JobTitleSearchContainer ref={ref}>
      <Icon24.Search />
      <JobTitleSearch
        placeholder={I18n.t('builder.use_example.job_title_placeholder')}
        value={searchedTitle}
        onChange={onTitleChange}
        onFocus={() => {
          trackInternalEvent(UseExampleEvents.clickSearchBar, {
            label: UseExampleEventLabels.examplesPrefill,
          })
          setShowSuggestedTitles(true)
        }}
      />
      {showSuggestedTitles && (
        <SuggestedTitlesContainer>
          <SuggestedTitleList>
            {suggestedTitles.map((title, index) => (
              <SuggestedTitleItem key={`${title} ${index}`} onClick={() => onSelectTitle(title)}>
                {title}
              </SuggestedTitleItem>
            ))}
          </SuggestedTitleList>
        </SuggestedTitlesContainer>
      )}
      {searchedTitle && !suggestedTitles.length && (
        <NoResultsContainer>
          <NoResults>
            <NoResultsImg src={emptySearch} />
            <NoResultsText>
              {I18n.t('builder.use_example.no_results.role_not_found')}{' '}
              <AnotherOptionBtn theme="ghost" onClick={onStartWithAnotherOption}>
                {I18n.t('builder.use_example.no_results.another_option')}
              </AnotherOptionBtn>
            </NoResultsText>
          </NoResults>
        </NoResultsContainer>
      )}
    </JobTitleSearchContainer>
  )
}

export default JobTitle
